import React from 'react';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { useTryPermissions } from '../contexts/PermissionContext';

/**
 * The four order-screen entities that have a Data Management maintenance table
 * behind them. Each maps to the route that edits it.
 */
export type EntityKind = 'location' | 'customer' | 'broker' | 'billto';

const ROUTES: Record<EntityKind, string> = {
  location: '/data-management/locations',
  customer: '/data-management/customers',
  broker: '/data-management/broker-forwarder',
  billto: '/data-management/bill-tos',
};

interface Props {
  kind: EntityKind;
  /** Resolved recno of the referenced record. Nothing renders when it's unset. */
  recno: number | string | null | undefined;
  /** Field name, used for the button's accessible name ("Edit Consignee"). */
  label: string;
}

/**
 * Pencil button next to an order's Consignee / Customer / Broker / Bill To,
 * opening that record's maintenance drawer in a new browser tab. Sibling to the
 * V1-parity `(i)` info icon (AddlInfoView's `InfoIconButton`) and follows the
 * same render rules: shown in edit AND read modes, hidden when the recno is 0.
 *
 * The destination pages are `PermissionGate`-wrapped, so the button is also
 * hidden when the user can't reach the route — otherwise the new tab would land
 * on a denial screen. When there's no PermissionProvider in the tree (isolated
 * previews, unit tests) we render: the gate on the destination still applies.
 *
 * The `?edit=<recno>` deep link is honored by TablemaintPage and LocationsEditor.
 */
const EntityEditLink: React.FC<Props> = ({ kind, recno, label }) => {
  const permissions = useTryPermissions();
  const route = ROUTES[kind];
  const id = Number(recno) || 0;

  if (id <= 0) return null;
  // No provider (isolated previews, unit tests) or a partial stub → render; the
  // destination route is PermissionGate-wrapped either way.
  if (typeof permissions?.hasAccess === 'function' && !permissions.hasAccess(route)) return null;

  return (
    <button
      type="button"
      onClick={() => window.open(`${route}?edit=${id}`, '_blank', 'noopener')}
      className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-slate-100 dark:hover:bg-gray-700 shrink-0"
      aria-label={`Edit ${label}`}
      title="Edit in table maintenance"
    >
      <PencilSquareIcon className="w-3.5 h-3.5 text-slate-600 dark:text-gray-300" />
    </button>
  );
};

export default EntityEditLink;
